import { Link } from 'react-router-dom';
import { featuredResources, beginnerSteps, articlePreviews } from '@/mocks/homeContent';

export function FeaturedSection() {
  return (
    <section id="guides" className="py-16 md:py-24 px-4 md:px-10 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs md:text-sm text-foreground-500 font-medium uppercase tracking-wider mb-2">
          Featured Resources
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-10 leading-tight">
          Featured Spreadsheet<br />Resources
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {featuredResources.map((res) => (
            <div
              key={res.title}
              className="bg-background-50 rounded-2xl p-6 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
            >
              <span className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                <i className={`${res.icon} text-accent-600 text-lg`}></i>
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                {res.title}
              </h3>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full font-medium">
                  {res.category}
                </span>
                <span className="text-xs text-foreground-500">{res.difficulty}</span>
              </div>
              <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">
                {res.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ArticlePreviewsSection() {
  return (
    <section className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs md:text-sm text-foreground-500 font-medium uppercase tracking-wider mb-2">
          SEO Articles & Guides
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
          In-Depth AllChinaBuy<br />Spreadsheet Guides
        </h2>
        <p className="text-foreground-600 text-sm md:text-base max-w-xl mb-10">
          Explore our collection of detailed guides covering everything from finding the best spreadsheets to staying safe while shopping. Each article is researched and written for the AllChinaBuy community.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articlePreviews.map((article) => (
            <Link
              key={article.slug}
              to={`/${article.slug}`}
              className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-200 hover:shadow-sm transition-all duration-300 cursor-pointer flex flex-col"
            >
              <div className="aspect-[8/5] overflow-hidden bg-background-100">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-4 md:p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${article.icon} text-accent-600 text-sm`}></i>
                  </span>
                  <span className="text-xs text-foreground-500 font-medium">SEO Guide</span>
                </div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 leading-snug">
                  {article.title}
                </h3>
                <p className="text-foreground-500 text-xs md:text-sm leading-relaxed flex-1">
                  {article.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 text-primary-600 text-xs font-medium mt-3 group-hover:gap-2 transition-all whitespace-nowrap">
                  Read Article
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/allchinabuy-buying-guide/"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-guide-line"></i>
            Shopping Guide
          </Link>
        </div>
      </div>
    </section>
  );
}

export function BeginnerGuide() {
  return (
    <section className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs md:text-sm text-foreground-500 font-medium uppercase tracking-wider mb-2">
          Getting Started
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
          Beginner&apos;s Guide to Using<br />an AllChinaBuy Spreadsheet
        </h2>
        <p className="text-foreground-600 text-sm md:text-base max-w-xl mb-12">
          New to AllChinaBuy? Follow these five simple steps to get started with your first spreadsheet and make the most of your shopping experience.
        </p>

        <div className="relative">
          <div className="hidden lg:block absolute left-8 top-8 bottom-8 w-px bg-background-300"></div>
          <div className="space-y-0">
            {beginnerSteps.map((step, idx) => (
              <div key={step.step} className="flex gap-5 md:gap-8 py-6 md:py-8">
                <div className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="font-heading text-xl font-bold text-background-50">{step.step}</span>
                </div>
                <div className="pt-2">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}